import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Modal from '../components/Modal';
import LineItemsTable from '../components/LineItemsTable';
import { QuickAddVendorModal } from '../components/QuickAddModal';
import Toast from '../components/Toast';

/**
 * Normalize a UoM abbreviation from OCR to a dropdown option value.
 * Maps common abbreviations (case-insensitive) to the <select> values
 * used in LineItemsTable.
 *
 * @param {string} raw - Raw UoM string from OCR (e.g. "CS", "lb", "EA").
 * @returns {string} Normalized value matching a dropdown option, or the original lowercased string.
 */
const normalizeUoM = (raw) => {
  if (!raw) return '';
  const lookup = raw.trim().toLowerCase();
  const UOM_MAP = {
    cs: 'case', case: 'case', cases: 'case',
    ea: 'each', each: 'each',
    lb: 'lb', lbs: 'lb', pound: 'lb', pounds: 'lb',
    dz: 'dozen', doz: 'dozen', dozen: 'dozen',
    bx: 'box', box: 'box', boxes: 'box',
    ct: 'carton', ctn: 'carton', carton: 'carton', cartons: 'carton',
    plt: 'pallet', pallet: 'pallet', pallets: 'pallet',
    gal: 'gal', gallon: 'gal', gallons: 'gal',
    kg: 'kg', kilogram: 'kg', kilograms: 'kg',
    l: 'liter', ltr: 'liter', liter: 'liter', liters: 'liter',
  };
  return UOM_MAP[lookup] || lookup;
};

/**
 * Parse a date string from OCR into ISO format (YYYY-MM-DD) for HTML date inputs.
 * Handles common formats: MM/DD/YYYY, M/D/YYYY, MM-DD-YYYY, YYYY-MM-DD.
 *
 * @param {string} dateStr - Raw date string from OCR.
 * @returns {string} ISO date string (YYYY-MM-DD), or empty string if unparseable.
 */
const parseOCRDate = (dateStr) => {
  if (!dateStr) return '';
  const trimmed = dateStr.trim();

  // Already ISO format (YYYY-MM-DD)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  // MM/DD/YYYY or M/D/YYYY (with / or - separator)
  const match = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    const month = match[1].padStart(2, '0');
    const day = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  }

  // Fallback: try Date.parse
  const parsed = new Date(trimmed);
  if (!isNaN(parsed.getTime())) {
    return parsed.toISOString().split('T')[0];
  }

  return '';
};

/**
 * Invoice entry page for manually recording a new invoice with line items and charges.
 * Supports pre-filling from OCR extraction via sessionStorage.
 *
 * @param {Object} props
 * @param {string|number} [props.activeBusiness] - Currently selected business ID from App header
 */
function InvoiceEntry({ activeBusiness }) {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [toast, setToast] = useState(null);
  const [showVendorModal, setShowVendorModal] = useState(false);
  const [isParentBusiness, setIsParentBusiness] = useState(false);
  const [childBusinesses, setChildBusinesses] = useState([]);
  const [selectedChildBusiness, setSelectedChildBusiness] = useState('');
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [formData, setFormData] = useState({
    business_id: activeBusiness || '',
    vendor_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    notes: '',
    tax: 0,
    fuel_surcharge: 0,
    fuel_surcharge_in_subtotal: false
  });
  const [lineItems, setLineItems] = useState([{ id: 0, sku: '', item_name: '', category_id: '', quantity: 1, unit_of_measure: '', unit_price: 0 }]);
  const [miscCharges, setMiscCharges] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  useEffect(() => {
    loadBusinesses();
    loadVendors();
    loadCategories();

    // Check for OCR-extracted data in sessionStorage
    const ocrData = sessionStorage.getItem('ocrExtractedData');
    if (ocrData) {
      try {
        const parsed = JSON.parse(ocrData);
        setFormData(prev => ({
          ...prev,
          vendor_id: parsed.vendor_id ? String(parsed.vendor_id) : prev.vendor_id,
          invoice_number: parsed.invoice_number || prev.invoice_number,
          invoice_date: parseOCRDate(parsed.invoice_date) || prev.invoice_date,
          notes: parsed.notes || prev.notes,
          tax: parsed.tax || prev.tax,
          fuel_surcharge: parsed.fuel_surcharge || prev.fuel_surcharge
        }));

        // Pre-fill line items if extracted from OCR
        let lineItemMsg = '';
        if (parsed.line_items && parsed.line_items.length > 0) {
          const mappedItems = parsed.line_items.map((item, idx) => ({
            id: idx,
            sku: item.sku || '',
            item_name: item.catalog_item_name || item.description || '',
            category_id: item.catalog_category_id || '',
            quantity: item.quantity != null ? item.quantity : 1,
            unit_of_measure: normalizeUoM(item.unit_of_measure) || item.catalog_default_uom || '',
            unit_price: item.unit_price != null ? item.unit_price : 0,
            actual_total: item.line_total != null ? parseFloat(item.line_total) || 0 : undefined,
          }));
          setLineItems(mappedItems);
          lineItemMsg = ` (${mappedItems.length} line item${mappedItems.length !== 1 ? 's' : ''})`;
        }

        sessionStorage.removeItem('ocrExtractedData');
        setToast({ message: `Invoice pre-filled with OCR data${lineItemMsg}. Please verify and complete.`, type: 'success' });
      } catch (error) {
        console.error('Error parsing OCR data:', error);
      }
    }

    // Check for existing invoice to edit (from InvoiceDetailModal)
    const editData = sessionStorage.getItem('editInvoiceData');
    if (editData) {
      sessionStorage.removeItem('editInvoiceData');
      try {
        const parsed = JSON.parse(editData);
        const invoiceId = parsed.id;
        setEditingInvoiceId(invoiceId);
        // Fetch fresh data from API to ensure accuracy
        api.getInvoice(invoiceId).then(res => {
          const inv = res.data;
          setFormData({
            business_id: inv.business_id ? String(inv.business_id) : '',
            vendor_id: inv.vendor_id ? String(inv.vendor_id) : '',
            invoice_number: inv.invoice_number || '',
            invoice_date: inv.invoice_date || new Date().toISOString().split('T')[0],
            notes: inv.notes || '',
            tax: inv.tax != null ? inv.tax : 0,
            fuel_surcharge: inv.fuel_surcharge != null ? inv.fuel_surcharge : 0,
            fuel_surcharge_in_subtotal: !!inv.fuel_surcharge_in_subtotal,
          });
          if (inv.line_items && inv.line_items.length > 0) {
            setLineItems(inv.line_items.map((item, idx) => ({
              id: idx,
              sku: item.sku || '',
              item_name: item.item_name || '',
              category_id: item.category_id || '',
              quantity: parseFloat(item.quantity) || 1,
              unit_of_measure: item.unit_of_measure || '',
              unit_price: parseFloat(item.unit_price) || 0,
            })));
          }
          if (inv.misc_charges && inv.misc_charges.length > 0) {
            setMiscCharges(inv.misc_charges.map((mc, idx) => ({
              id: idx,
              description: mc.description || '',
              amount: parseFloat(mc.amount) || 0,
            })));
          }
          setToast({ message: `Editing Invoice #${inv.invoice_number || invoiceId}`, type: 'success' });
        }).catch(err => {
          console.error('Error loading invoice for edit:', err);
          setToast({ message: 'Failed to load invoice for editing', type: 'error' });
          setEditingInvoiceId(null);
        });
      } catch (error) {
        console.error('Error parsing edit invoice data:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (activeBusiness) {
      setFormData(prev => ({ ...prev, business_id: activeBusiness }));
    }
  }, [activeBusiness]);

  /**
   * Check if the active business is a parent and load its children.
   * Auto-selects the child if there is only one child location.
   */
  useEffect(() => {
    const checkParent = async () => {
      if (!activeBusiness) {
        setIsParentBusiness(false);
        return;
      }
      try {
        const res = await api.getBusinessChildren(activeBusiness);
        if (res.data.length > 0) {
          setIsParentBusiness(true);
          setChildBusinesses(res.data);
          if (res.data.length === 1) {
            setSelectedChildBusiness(String(res.data[0].id));
          }
        } else {
          setIsParentBusiness(false);
          setChildBusinesses([]);
        }
      } catch (err) {
        setIsParentBusiness(false);
      }
    };
    checkParent();
  }, [activeBusiness]);

  const loadBusinesses = async () => {
    try {
      const response = await api.getBusinesses();
      setBusinesses(response.data);
    } catch (error) {
      console.error('Error loading businesses:', error);
      setToast({ message: 'Failed to load businesses', type: 'error' });
    }
  };

  const loadVendors = async () => {
    try {
      const response = await api.getVendors();
      setVendors(response.data);
    } catch (error) {
      console.error('Error loading vendors:', error);
      setToast({ message: 'Failed to load vendors', type: 'error' });
    }
  };

  const loadCategories = async () => {
    try {
      const response = await api.getLevel3Categories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
      setToast({ message: 'Failed to load categories', type: 'error' });
    }
  };

  const handleVendorCreated = (newVendor) => {
    loadVendors();
    setFormData(prev => ({ ...prev, vendor_id: String(newVendor.id) }));
  };

  const handleCategoryCreated = () => {
    loadCategories();
  };

  /**
   * Find a vendor by ID, handling string/number type coercion from form selects.
   *
   * @param {string|number} vendorId - The vendor ID to look up
   * @returns {Object|undefined} The vendor object, or undefined if not found
   */
  const findVendor = (vendorId) => vendors.find(v => String(v.id) === String(vendorId));

  const handleVendorChange = (e) => {
    const value = e.target.value;
    if (value === '__add_new__') {
      setShowVendorModal(true);
    } else {
      const vendor = findVendor(value);
      setFormData({
        ...formData,
        vendor_id: value,
        fuel_surcharge_in_subtotal: vendor ? !!vendor.fuel_surcharge_in_subtotal : false
      });
    }
  };

  /**
   * Derive whether the selected vendor uses SKU numbers.
   * @returns {boolean} True if the vendor uses SKUs (default true)
   */
  const vendorUsesSku = (() => {
    if (!formData.vendor_id || vendors.length === 0) return true;
    const vendor = findVendor(formData.vendor_id);
    return vendor ? vendor.uses_sku !== 0 : true;
  })();

  /**
   * Memoized invoice totals calculation. Recalculates only when line items,
   * misc charges, tax, or fuel surcharge values change.
   *
   * @returns {{ lineItemsTotal: number, miscTotal: number, subtotal: number, tax: number, fuelSurcharge: number, total: number }}
   */
  const calculateTotals = useMemo(() => {
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      // Prefer actual_total (OCR-extracted invoice amount) over qty * unit_price
      if (item.actual_total != null) return sum + item.actual_total;
      return sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
    }, 0);
    const miscTotal = miscCharges.reduce((sum, charge) =>
      sum + (parseFloat(charge.amount) || 0), 0);
    const tax = parseFloat(formData.tax) || 0;
    const fuelSurcharge = parseFloat(formData.fuel_surcharge) || 0;
    const fuelInSubtotal = formData.fuel_surcharge_in_subtotal;
    const subtotal = fuelInSubtotal
      ? lineItemsTotal + miscTotal + fuelSurcharge
      : lineItemsTotal + miscTotal;
    const total = fuelInSubtotal
      ? subtotal + tax
      : subtotal + tax + fuelSurcharge;

    return { lineItemsTotal, miscTotal, subtotal, tax, fuelSurcharge, total };
  }, [lineItems, miscCharges, formData.tax, formData.fuel_surcharge, formData.fuel_surcharge_in_subtotal]);

  const [saving, setSaving] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [editingTaxFuel, setEditingTaxFuel] = useState(false);
  const totalsBarRef = useRef(null);

  // Force browser repaint on sticky totals bar when values change
  useEffect(() => {
    const el = totalsBarRef.current;
    if (el) {
      el.style.display = 'none';
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;
      el.style.display = '';
    }
  }, [calculateTotals]);

  /**
   * Validate and submit the invoice form. Prevents double-submission by
   * disabling the button while the API call is in flight.
   *
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // Field-level validation
    const errors = {};
    if (!formData.business_id) errors.business_id = true;
    if (!formData.vendor_id) errors.vendor_id = true;
    if (!formData.invoice_date) errors.invoice_date = true;
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setToast({ message: 'Please fill in all required fields', type: 'error' });
      return;
    }

    const validLineItems = lineItems.filter(item =>
      item.item_name && item.quantity && item.unit_price
    );

    if (validLineItems.length === 0) {
      setToast({ message: 'Please add at least one line item', type: 'error' });
      return;
    }

    setSaving(true);
    const totals = calculateTotals;

    const invoiceData = {
      ...formData,
      business_id: parseInt(selectedChildBusiness || formData.business_id),
      vendor_id: parseInt(formData.vendor_id),
      tax: totals.tax,
      fuel_surcharge: totals.fuelSurcharge,
      line_items: validLineItems.map(item => ({
        sku: item.sku || '',
        item_name: item.item_name,
        category_id: item.category_id || null,
        quantity: parseFloat(item.quantity),
        unit_of_measure: item.unit_of_measure || '',
        unit_price: parseFloat(item.unit_price),
        total_price: item.actual_total != null ? item.actual_total : parseFloat(item.quantity) * parseFloat(item.unit_price),
      })),
      misc_charges: miscCharges.map(charge => ({
        description: charge.description,
        amount: parseFloat(charge.amount)
      }))
    };

    try {
      if (editingInvoiceId) {
        await api.updateInvoice(editingInvoiceId, invoiceData);
        // Navigate back to invoices list and re-open the detail modal
        navigate('../invoices', { state: { openInvoiceId: editingInvoiceId, toast: { message: 'Invoice updated successfully!', type: 'success' } } });
        return;
      } else {
        await api.createInvoice(invoiceData);
        setToast({ message: 'Invoice saved successfully!', type: 'success' });
      }

      // Reset form (new invoice creation only)
      setEditingInvoiceId(null);
      setFormData({
        business_id: activeBusiness || '',
        vendor_id: '',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        notes: '',
        tax: 0,
        fuel_surcharge: 0,
        fuel_surcharge_in_subtotal: false
      });
      setLineItems([{ id: 0, sku: '', item_name: '', category_id: '', quantity: 1, unit_of_measure: '', unit_price: 0 }]);
      setMiscCharges([]);

    } catch (error) {
      setToast({ message: error.response?.data?.error || 'Error saving invoice', type: 'error' });
      console.error('Error saving invoice:', error);
    } finally {
      setSaving(false);
    }
  };

  const totals = calculateTotals;

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">{editingInvoiceId ? 'Edit Invoice' : 'Enter Invoice'}</h1>
        <p className="page-subtitle">{editingInvoiceId ? 'Modify invoice details, line items, and charges' : 'Record a new invoice with line items and charges'}</p>
      </div>

      {activeBusiness && isParentBusiness && (
        <div className="info-message" style={{ marginBottom: '1rem' }}>
          <strong>Select a location:</strong> Invoices must be created for a specific location, not the parent business.
          <div style={{ marginTop: '0.5rem' }}>
            <select
              name="child_business_id"
              value={selectedChildBusiness}
              onChange={(e) => setSelectedChildBusiness(e.target.value)}
              aria-label="Select child business location"
            >
              <option value="">Choose a location...</option>
              {childBusinesses.map(child => (
                <option key={child.id} value={child.id}>{child.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Invoice Details — read-only header with Edit button */}
      <div className="card invoice-entry__header">
        <div className="invoice-entry__details-row">
          <div className="invoice-entry__detail-item">
            <span className="invoice-entry__detail-label">Business</span>
            <span className="invoice-entry__detail-value">{businesses.find(b => String(b.id) === String(formData.business_id))?.name || '—'}</span>
          </div>
          <div className="invoice-entry__detail-item">
            <span className="invoice-entry__detail-label">Vendor</span>
            <span className="invoice-entry__detail-value">{findVendor(formData.vendor_id)?.name || '—'}</span>
          </div>
          <div className="invoice-entry__detail-item">
            <span className="invoice-entry__detail-label">Invoice #</span>
            <span className="invoice-entry__detail-value" style={{ fontFamily: 'var(--font-m)' }}>{formData.invoice_number || '—'}</span>
          </div>
          <div className="invoice-entry__detail-item">
            <span className="invoice-entry__detail-label">Date</span>
            <span className="invoice-entry__detail-value">{formData.invoice_date || '—'}</span>
          </div>
          {formData.notes && (
            <div className="invoice-entry__detail-item invoice-entry__notes-field">
              <span className="invoice-entry__detail-label">Notes</span>
              <span className="invoice-entry__detail-value">{formData.notes}</span>
            </div>
          )}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingDetails(true)}>Edit Details</button>
        </div>
      </div>

      {/* Edit Invoice Details Modal */}
      <Modal isOpen={editingDetails} onClose={() => setEditingDetails(false)} title="Invoice Details" size="md">
        <div className="field">
          <label className="label" htmlFor="invoice-business">Business <span className="req">*</span></label>
          <select
            id="invoice-business"
            className="input"
            value={formData.business_id}
            onChange={(e) => { setFormData({ ...formData, business_id: e.target.value }); setFieldErrors(prev => ({ ...prev, business_id: false })); }}
            required
            aria-invalid={!!fieldErrors.business_id}
          >
            <option value="">Select...</option>
            {businesses.map(b => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="invoice-vendor">Vendor <span className="req">*</span></label>
          <select
            id="invoice-vendor"
            className="input"
            value={formData.vendor_id}
            onChange={(e) => { handleVendorChange(e); setFieldErrors(prev => ({ ...prev, vendor_id: false })); }}
            required
            aria-invalid={!!fieldErrors.vendor_id}
          >
            <option value="">Select...</option>
            {vendors.map(v => (
              <option key={v.id} value={v.id}>{v.name}</option>
            ))}
            <option value="__add_new__">+ Add New Vendor...</option>
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp3)' }}>
          <div className="field">
            <label className="label" htmlFor="invoice-number">Invoice #</label>
            <input
              id="invoice-number"
              className="input"
              type="text"
              value={formData.invoice_number}
              onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
              placeholder="INV-001"
            />
          </div>
          <div className="field">
            <label className="label" htmlFor="invoice-date">Date <span className="req">*</span></label>
            <input
              id="invoice-date"
              className="input"
              type="date"
              value={formData.invoice_date}
              onChange={(e) => { setFormData({ ...formData, invoice_date: e.target.value }); setFieldErrors(prev => ({ ...prev, invoice_date: false })); }}
              required
              aria-invalid={!!fieldErrors.invoice_date}
            />
          </div>
        </div>
        <div className="field">
          <label className="label" htmlFor="invoice-notes">Notes</label>
          <input
            id="invoice-notes"
            className="input"
            type="text"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Optional notes..."
          />
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp2)', marginTop: 'var(--sp4)' }}>
          <button type="button" className="btn btn-primary" onClick={() => setEditingDetails(false)}>Done</button>
        </div>
      </Modal>

      <form onSubmit={handleSubmit}>

        {/* Line Items + Misc Charges */}
        <div className="card invoice-entry__section">
          <LineItemsTable
            lineItems={lineItems}
            setLineItems={setLineItems}
            categories={categories}
            vendorId={formData.vendor_id}
            vendorName={findVendor(formData.vendor_id)?.name}
            onCategoryCreated={handleCategoryCreated}
            showSku={vendorUsesSku}
            miscCharges={miscCharges}
            setMiscCharges={setMiscCharges}
          />
        </div>

        {/* Totals Bar — compact read-only */}
        <div className="card invoice-entry__totals-bar" ref={totalsBarRef}>
          <div className="invoice-entry__totals-row">
            <span className="invoice-entry__total-item">
              <span className="invoice-entry__total-label">Items</span>
              <span className="td-amt">${totals.lineItemsTotal.toFixed(2)}</span>
            </span>
            {totals.miscTotal > 0 && (
              <span className="invoice-entry__total-item">
                <span className="invoice-entry__total-label">Misc</span>
                <span className="td-amt">${totals.miscTotal.toFixed(2)}</span>
              </span>
            )}
            <span className="invoice-entry__total-item">
              <span className="invoice-entry__total-label">Subtotal</span>
              <span className="td-amt" style={{ fontWeight: 600 }}>${totals.subtotal.toFixed(2)}</span>
            </span>
            <span className="invoice-entry__total-item">
              <span className="invoice-entry__total-label">Tax</span>
              <span className="td-amt">${totals.tax.toFixed(2)}</span>
            </span>
            <span className="invoice-entry__total-item">
              <span className="invoice-entry__total-label">Fuel</span>
              <span className="td-amt">
                ${totals.fuelSurcharge.toFixed(2)}
                {formData.fuel_surcharge_in_subtotal && <span style={{ fontSize: '0.7rem', color: 'var(--st500)', marginLeft: 'var(--sp1)' }}>(in sub)</span>}
              </span>
            </span>
            <span className="invoice-entry__total-item invoice-entry__total-item--grand">
              <span className="invoice-entry__total-label">Total</span>
              <span className="td-amt" style={{ fontWeight: 700, fontSize: '1.1rem' }}>${totals.total.toFixed(2)}</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp2)', alignItems: 'center' }}>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditingTaxFuel(true)} style={{ whiteSpace: 'nowrap' }}>
              Edit Tax/Fuel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving} style={{ whiteSpace: 'nowrap' }}>
              {saving ? 'Saving...' : (editingInvoiceId ? 'Update Invoice' : 'Save Invoice')}
            </button>
          </div>
        </div>

        {/* Edit Tax & Fuel Modal */}
        <Modal isOpen={editingTaxFuel} onClose={() => setEditingTaxFuel(false)} title="Tax & Fuel Surcharge" size="sm">
          <div className="field">
            <label className="label" htmlFor="modal-tax">Tax Amount</label>
            <input id="modal-tax" className="input" type="number" step="0.01" min="0" value={formData.tax} onChange={(e) => setFormData({ ...formData, tax: e.target.value })} style={{ fontFamily: 'var(--font-m)' }} />
          </div>
          <div className="field">
            <label className="label" htmlFor="modal-fuel">Fuel Surcharge</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp3)' }}>
              <input id="modal-fuel" className="input" type="number" step="0.01" min="0" value={formData.fuel_surcharge} onChange={(e) => setFormData({ ...formData, fuel_surcharge: e.target.value })} style={{ fontFamily: 'var(--font-m)', flex: 1 }} />
              <label className="settings-form__checkbox" style={{ whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={formData.fuel_surcharge_in_subtotal} onChange={(e) => setFormData({ ...formData, fuel_surcharge_in_subtotal: e.target.checked })} />
                In subtotal
              </label>
            </div>
          </div>
          <div style={{ marginTop: 'var(--sp4)' }}>
            <button type="button" className="btn btn-primary" onClick={() => setEditingTaxFuel(false)}>Done</button>
          </div>
        </Modal>
      </form>

      <QuickAddVendorModal
        isOpen={showVendorModal}
        onClose={() => setShowVendorModal(false)}
        onVendorCreated={handleVendorCreated}
      />
    </div>
  );
}

InvoiceEntry.propTypes = {
  /** Currently selected business ID from App header */
  activeBusiness: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default InvoiceEntry;
